import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Loader2, ExternalLink, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LandingQuickActions from "../components/LandingQuickActions";
import { useCompanyPhotos } from "../components/CompanyPositioning";

const ARIAL = { fontFamily: "Arial, Helvetica, sans-serif" };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [notice, setNotice] = useState("");
  const photos = useCompanyPhotos();

  useEffect(() => {
    if (sessionStorage.getItem("session_ended")) {
      sessionStorage.removeItem("session_ended");
      setNotice("La sessione precedente è stata chiusa perché è stato effettuato un nuovo accesso con questo account.");
    }
  }, []);

  useEffect(() => {
    if (photos.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), 4000);
    return () => clearInterval(t);
  }, [photos.length]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-in-up">
          <div className="flex items-center gap-2 mb-10">
            <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-10 w-10 rounded-full object-contain" />
            <div className="leading-tight">
              <div className="font-heading font-extrabold tracking-wide">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">Div. Chiavi in mano</div>
            </div>
          </div>

          <div className="inline-block text-[10px] uppercase tracking-[0.18em] text-primary font-bold mb-3">Configuratore online</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight mb-3 leading-tight">
            Configura il tuo impianto fotovoltaico <span className="text-primary">chiavi in mano</span>, con i pannelli Made in Italy di TORRI.solar
          </h1>
          <p className="text-sm text-muted-foreground mb-6">In pochi minuti, in autonomia: profilazione, analisi della bolletta, misurazione del tetto e la tua analisi personalizzata. Poi prenoti una consulenza con un nostro esperto.</p>
          <a href="/configura" data-testid="cta-configura"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:opacity-90 transition-opacity">
            Configura il tuo impianto <ArrowRight className="h-4 w-4" />
          </a>

          <LandingQuickActions />

          <details className="mt-10 group" data-testid="reserved-area" open={!!notice}>
            <summary className="cursor-pointer list-none text-xs text-muted-foreground hover:text-foreground transition-colors select-none">
              Accedi all'area riservata →
            </summary>
            <form onSubmit={submit} className="space-y-4 mt-5 max-w-sm">
              {notice && (
                <p className="text-sm text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2" data-testid="login-notice">
                  {notice}
                </p>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="nome@torrisolare.it" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full font-semibold">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Accedi
              </Button>
            </form>
          </details>
        </div>
      </div>

      {/* Right cover — slider fabbrica + posizionamento */}
      <div className="hidden lg:block relative overflow-hidden" data-testid="login-slider">
        {photos.map((p, i) => (
          <img
            key={i}
            src={p.url}
            alt={p.alt}
            data-testid={`login-slide-${i}`}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/10" />
        <div className="absolute bottom-10 left-10 right-10" style={ARIAL}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-bold mb-3">La nostra azienda</div>
          <p className="text-xl font-bold leading-snug max-w-lg" style={ARIAL}>
            Dal 2007, la prima fabbrica di pannelli fotovoltaici Made in Italy con la garanzia del chiavi in mano diretto al cliente finale.
          </p>
          <p className="text-sm text-muted-foreground mt-3 max-w-lg leading-snug" style={ARIAL}>
            I nostri pannelli fotovoltaici uniscono tecnica costruttiva e design industriale per diventare i pannelli fotovoltaici più robusti e sicuri del mercato.
          </p>
          <a href="https://www.torri.solar" target="_blank" rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline" style={ARIAL}>
            Scopri le nostre tecnologie e la nostra storia su www.torri.solar
            <ExternalLink className="h-3 w-3" />
          </a>
          <div className="mt-5 flex gap-1.5">
            {photos.map((_, i) => (
              <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Vai alla foto ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-primary" : "w-1.5 bg-white/40"}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
